import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Users, ChevronRight, Home, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { expandStateName } from "@/data/usStates";

/**
 * FindNeighborhood — public 3-level browse (Nextdoor "find-neighborhood" mirror).
 *
 * Route shape:
 *   /find-neighborhood                         → states index
 *   /find-neighborhood/:stateNorm              → cities in state
 *   /find-neighborhood/:stateNorm/:cityNorm    → ZIP codes in city
 *   /find-neighborhood/:stateNorm/:cityNorm/:zip → neighborhoods in ZIP
 *
 * Public — no auth required to browse. Joining a neighborhood requires
 * customer auth (button links to /login when signed out).
 */
export default function FindNeighborhood() {
  const params = useParams();
  const { stateNorm, cityNorm, zip } = params;

  if (stateNorm && cityNorm && zip) return <NeighborhoodListLevel stateNorm={stateNorm} cityNorm={cityNorm} zip={zip} />;
  if (stateNorm && cityNorm) return <CityLevel stateNorm={stateNorm} cityNorm={cityNorm} />;
  if (stateNorm) return <StateLevel stateNorm={stateNorm} />;
  return <StateIndex />;
}

/* ---------- Level 1: state index ---------- */
function StateIndex() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/directory/neighborhoods");
        setData(r.data);
      } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  // Group states alphabetically A-Z by their expanded (full) name so
  // grouping and letter labels reflect the display name, not any 2-letter
  // abbreviation stored in the DB.
  const groupedByLetter = () => {
    const g = {};
    (data?.states || []).forEach((s) => {
      const full = expandStateName(s.state);
      const letter = (full || "?")[0].toUpperCase();
      if (!g[letter]) g[letter] = [];
      g[letter].push({ ...s, display: full });
    });
    // Sort each bucket by display name so A → Z within each letter group.
    Object.values(g).forEach((arr) => arr.sort((a, b) => (a.display || "").localeCompare(b.display || "")));
    return g;
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10" data-testid="find-neighborhood-index">
      <Breadcrumbs items={[{ label: "Find your neighborhood" }]} />
      <header className="mt-4">
        <div className="overline text-[#B31E1E]">Community</div>
        <h1 className="font-display text-4xl tracking-tighter mt-1">Find your neighborhood</h1>
        <p className="mt-2 text-gray-600 max-w-2xl">
          Browse neighborhoods by state and city. Ask a question, recommend a
          business, share alerts and lost-and-found — with only the neighbors
          who live where you live.
        </p>
      </header>

      {error ? (
        <Card className="rounded-md p-8 mt-6 text-red-600" data-testid="find-error">{error}</Card>
      ) : !data ? (
        <p className="text-sm text-gray-500 mt-8">Loading…</p>
      ) : data.total === 0 ? (
        <EmptyStart />
      ) : (
        <div className="mt-8 space-y-8" data-testid="states-grouped">
          {Object.entries(groupedByLetter()).sort(([a], [b]) => a.localeCompare(b)).map(([letter, states]) => (
            <section key={letter} className="border-t border-border pt-6">
              <div className="text-2xl font-display text-[#B31E1E]">{letter}</div>
              <ul className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {states.map((s) => (
                  <li key={s.state_norm}>
                    <Link
                      to={`/find-neighborhood/${encodeURIComponent(s.state_norm)}`}
                      className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-[#B31E1E]/5 hover:text-[#B31E1E] group"
                      data-testid={`state-${s.state_norm}`}
                    >
                      <span className="truncate">
                        {s.display}
                        <span className="ml-1 text-xs text-gray-500 group-hover:text-[#B31E1E]/60">({s.count})</span>
                      </span>
                      <ChevronRight size={12} className="text-gray-400 group-hover:text-[#B31E1E]" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Level 2: cities in a state ---------- */
function StateLevel({ stateNorm }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/directory/neighborhoods/${encodeURIComponent(stateNorm)}`);
        setData(r.data);
      } catch (e) {
        if (e.response?.status === 404) setError("No neighborhoods listed in this state yet.");
        else setError(formatApiError(e.response?.data?.detail));
      }
    })();
  }, [stateNorm]);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-10" data-testid="state-level">
      <Breadcrumbs
        items={[
          { label: "Find your neighborhood", href: "/find-neighborhood" },
          { label: expandStateName(data?.state || stateNorm) },
        ]}
      />
      <header className="mt-4">
        <h1 className="font-display text-3xl tracking-tighter">Cities in {expandStateName(data?.state || stateNorm)}</h1>
        <p className="mt-2 text-sm text-gray-600">Choose a city to see its neighborhoods.</p>
      </header>

      {error ? (
        <Card className="rounded-md p-8 mt-6 text-gray-600">
          <p>{error}</p>
          <div className="mt-4">
            <Button variant="outline" onClick={() => nav("/find-neighborhood")} className="rounded-[35px]">Back to all states</Button>
          </div>
        </Card>
      ) : !data ? (
        <p className="text-sm text-gray-500 mt-8">Loading…</p>
      ) : (
        <ul className="mt-6 divide-y divide-border rounded-md border border-border overflow-hidden" data-testid="cities-list">
          {data.cities.map((c) => (
            <li key={c.city_norm}>
              <Link
                to={`/find-neighborhood/${encodeURIComponent(stateNorm)}/${encodeURIComponent(c.city_norm)}`}
                className="flex items-center justify-between px-4 py-3 hover:bg-[#B31E1E]/5 hover:text-[#B31E1E] group"
                data-testid={`city-${c.city_norm}`}
              >
                <span className="min-w-0 truncate">
                  <MapPin size={12} className="inline mr-2 text-gray-400" />
                  {c.city}
                </span>
                <span className="text-xs text-gray-500 group-hover:text-[#B31E1E]/60 flex items-center gap-3 shrink-0">
                  <span>{c.count} group{c.count === 1 ? "" : "s"}</span>
                  <ChevronRight size={12} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Level 3: neighborhoods in a city (with ZIP chips + search) ---------- */
function CityLevel({ stateNorm, cityNorm }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [zips, setZips] = useState([]);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [neighborhoods, zipsRes] = await Promise.all([
        api.get(`/directory/neighborhoods/${encodeURIComponent(stateNorm)}/${encodeURIComponent(cityNorm)}`),
        api.get(`/directory/neighborhoods/${encodeURIComponent(stateNorm)}/${encodeURIComponent(cityNorm)}/zips`).catch(() => ({ data: { zips: [] } })),
      ]);
      setData(neighborhoods.data);
      setZips(zipsRes.data?.zips || []);
    } catch (e) {
      if (e.response?.status === 404) setError("No neighborhoods listed in this city yet.");
      else setError(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [stateNorm, cityNorm]);

  const join = async (gid) => {
    if (!user) { nav("/login?next=" + encodeURIComponent(window.location.pathname)); return; }
    if (user.role !== "customer") { toast.error("Neighborhood boards are customer-only."); return; }
    try {
      await api.post(`/neighborhoods/${gid}/join`);
      toast.success("Joined — head to /neighborhood to post.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Client-side name/description search — folded into the same rendered list
  // so users can either scan the ZIP chips or type-to-filter.
  const needle = search.trim().toLowerCase();
  const visible = (data?.items || []).filter((n) => {
    if (!needle) return true;
    return (
      (n.name || "").toLowerCase().includes(needle) ||
      (n.description || "").toLowerCase().includes(needle) ||
      (n.zip_codes || []).some((z) => (z || "").includes(needle))
    );
  });

  const stateDisplay = expandStateName(data?.state || stateNorm);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-10" data-testid="city-level">
      <Breadcrumbs
        items={[
          { label: "Find your neighborhood", href: "/find-neighborhood" },
          { label: stateDisplay, href: `/find-neighborhood/${stateNorm}` },
          { label: data?.city || cityNorm },
        ]}
      />
      <header className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">Neighborhoods in {data?.city || cityNorm}, {stateDisplay}</h1>
          <p className="mt-2 text-sm text-gray-600">Filter by ZIP code or search by name.</p>
        </div>
        {user?.role === "customer" && (
          <Link to="/neighborhood">
            <Button variant="outline" className="rounded-[35px]">
              <Home size={13} className="mr-2" /> My neighborhood
            </Button>
          </Link>
        )}
      </header>

      {/* ZIP filter chips — click one to jump into the ZIP drill-down */}
      {zips.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="zip-chips">
          <span className="text-[11px] font-mono uppercase tracking-wider text-gray-500 self-center mr-1">Browse by ZIP</span>
          {zips.map((z) => (
            <Link
              key={z.zip}
              to={`/find-neighborhood/${stateNorm}/${cityNorm}/${encodeURIComponent(z.zip)}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1 text-xs font-mono hover:border-[#B31E1E] hover:text-[#B31E1E]"
              data-testid={`zip-chip-${z.zip}`}
            >
              {z.zip}
              <span className="text-[10px] text-gray-500">· {z.count}</span>
            </Link>
          ))}
        </div>
      )}

      {/* Search — filters the neighborhood list client-side by name/description/zip */}
      <div className="mt-5 relative max-w-md" data-testid="neighborhood-search-wrap">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search neighborhoods by name or ZIP…"
          className="rounded-sm pl-9"
          data-testid="neighborhood-search-input"
        />
      </div>

      {error ? (
        <Card className="rounded-md p-8 mt-6 text-gray-600">
          <p>{error}</p>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => nav(`/find-neighborhood/${stateNorm}`)} className="rounded-[35px]">Back to state</Button>
            {user?.role === "customer" && (
              <Link to="/neighborhood"><Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white">Create your neighborhood</Button></Link>
            )}
          </div>
        </Card>
      ) : !data ? (
        <p className="text-sm text-gray-500 mt-8">Loading…</p>
      ) : visible.length === 0 ? (
        <Card className="rounded-md p-8 mt-6 text-center text-gray-600" data-testid="no-search-results">
          No neighborhoods match &ldquo;{search}&rdquo;.
        </Card>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="neighborhoods-list">
          {visible.map((n) => <NeighborhoodCard key={n.id} n={n} onJoin={join} />)}
        </div>
      )}
    </div>
  );
}


/* ---------- Level 4: neighborhoods in a single ZIP ---------- */
function NeighborhoodListLevel({ stateNorm, cityNorm, zip }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const r = await api.get(
        `/directory/neighborhoods/${encodeURIComponent(stateNorm)}/${encodeURIComponent(cityNorm)}`,
        { params: { zip } },
      );
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 404) setError(`No neighborhoods listed in ${zip} yet.`);
      else setError(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [stateNorm, cityNorm, zip]);

  const join = async (gid) => {
    if (!user) { nav("/login?next=" + encodeURIComponent(window.location.pathname)); return; }
    if (user.role !== "customer") { toast.error("Neighborhood boards are customer-only."); return; }
    try {
      await api.post(`/neighborhoods/${gid}/join`);
      toast.success("Joined — head to /neighborhood to post.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const needle = search.trim().toLowerCase();
  const visible = (data?.items || []).filter((n) => {
    if (!needle) return true;
    return (
      (n.name || "").toLowerCase().includes(needle) ||
      (n.description || "").toLowerCase().includes(needle)
    );
  });
  const stateDisplay = expandStateName(data?.state || stateNorm);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-10" data-testid="zip-level">
      <Breadcrumbs
        items={[
          { label: "Find your neighborhood", href: "/find-neighborhood" },
          { label: stateDisplay, href: `/find-neighborhood/${stateNorm}` },
          { label: data?.city || cityNorm, href: `/find-neighborhood/${stateNorm}/${cityNorm}` },
          { label: `ZIP ${zip}` },
        ]}
      />
      <header className="mt-4">
        <h1 className="font-display text-3xl tracking-tighter">Neighborhoods in ZIP {zip}</h1>
        <p className="mt-2 text-sm text-gray-600">{data?.city || cityNorm}, {stateDisplay}</p>
      </header>

      <div className="mt-5 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search neighborhoods in this ZIP…"
          className="rounded-sm pl-9"
          data-testid="zip-search-input"
        />
      </div>

      {error ? (
        <Card className="rounded-md p-8 mt-6 text-gray-600">
          <p>{error}</p>
          <Button variant="outline" onClick={() => nav(`/find-neighborhood/${stateNorm}/${cityNorm}`)} className="rounded-[35px] mt-4">Back to city</Button>
        </Card>
      ) : !data ? (
        <p className="text-sm text-gray-500 mt-8">Loading…</p>
      ) : visible.length === 0 ? (
        <Card className="rounded-md p-8 mt-6 text-center text-gray-600" data-testid="no-zip-search-results">
          No neighborhoods match &ldquo;{search}&rdquo;.
        </Card>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="zip-neighborhoods-list">
          {visible.map((n) => <NeighborhoodCard key={n.id} n={n} onJoin={join} />)}
        </div>
      )}
    </div>
  );
}


// Reusable neighborhood card so the city + zip levels share visuals
function NeighborhoodCard({ n, onJoin }) {
  return (
    <Card className="rounded-md p-5 hover:border-[#B31E1E]/40 transition-colors" data-testid={`nb-card-${n.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-lg truncate">{n.name}</div>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
            <span className="inline-flex items-center gap-1"><Users size={11} /> {n.member_count}</span>
            {n.zip_codes?.length > 0 && (
              <span className="truncate">· {n.zip_codes.slice(0, 3).join(", ")}{n.zip_codes.length > 3 ? "…" : ""}</span>
            )}
          </div>
          {n.description && (
            <p className="mt-2 text-sm text-gray-700 line-clamp-2">{n.description}</p>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => onJoin(n.id)}
          className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white shrink-0"
          data-testid={`join-${n.id}`}
        >
          Join
        </Button>
      </div>
    </Card>
  );
}

/* ---------- Shared ---------- */

function Breadcrumbs({ items }) {
  return (
    <nav className="text-xs font-mono text-gray-500 flex flex-wrap items-center gap-1" data-testid="breadcrumbs">
      {items.map((it, i) => (
        <span key={`crumb-${i}-${it.label}`} className="flex items-center gap-1">
          {it.href ? (
            <Link to={it.href} className="hover:text-[#B31E1E]">{it.label}</Link>
          ) : (
            <span className="text-gray-700">{it.label}</span>
          )}
          {i < items.length - 1 && <ChevronRight size={11} className="text-gray-300" />}
        </span>
      ))}
    </nav>
  );
}

function EmptyStart() {
  const { user } = useAuth();
  return (
    <Card className="rounded-md p-10 mt-8 text-center" data-testid="empty-directory">
      <Users size={30} className="mx-auto text-gray-300 mb-3" />
      <h2 className="font-display text-2xl">No neighborhoods yet — start one</h2>
      <p className="mt-2 text-gray-600 max-w-md mx-auto">
        Servrevu neighborhoods are created by real people. Be the first on your block.
      </p>
      <div className="mt-5">
        {user?.role === "customer" ? (
          <Link to="/neighborhood"><Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white">Create the first neighborhood</Button></Link>
        ) : (
          <Link to="/register"><Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white">Sign up as a customer to start one</Button></Link>
        )}
      </div>
    </Card>
  );
}
